import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export type TierName = 'free' | 'creator' | 'studio';
export type BillingInterval = 'monthly' | 'yearly';
export type TokenBundle = 15 | 100 | 300 | null;

interface TierContextType {
  selectedTier: TierName;
  billingInterval: BillingInterval;
  selectedBundle: TokenBundle;
  setTierSelection: (tier: TierName, interval: BillingInterval) => void;
  setBundleSelection: (bundle: TokenBundle) => void;
  clearTierSelection: () => void;
}

const TierContext = createContext<TierContextType | undefined>(undefined);

export function TierProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage if available
  const [selectedTier, setSelectedTier] = useState<TierName>(() => {
    const saved = localStorage.getItem('selectedTier');
    return (saved === 'creator' || saved === 'studio' || saved === 'free') ? saved : 'free';
  });

  const [billingInterval, setBillingInterval] = useState<BillingInterval>(() => {
    const saved = localStorage.getItem('billingInterval');
    return (saved === 'yearly' || saved === 'monthly') ? saved : 'monthly';
  });

  const [selectedBundle, setSelectedBundle] = useState<TokenBundle>(() => {
    const saved = localStorage.getItem('selectedBundle');
    return (saved === '15' || saved === '100' || saved === '300') ? parseInt(saved) as TokenBundle : null;
  });

  const setTierSelection = (tier: TierName, interval: BillingInterval) => {
    setSelectedTier(tier);
    setBillingInterval(interval);
    localStorage.setItem('selectedTier', tier);
    localStorage.setItem('billingInterval', interval);
  };

  const setBundleSelection = (bundle: TokenBundle) => {
    setSelectedBundle(bundle);
    if (bundle === null) {
      localStorage.removeItem('selectedBundle');
    } else {
      localStorage.setItem('selectedBundle', bundle.toString());
    }
  };

  const clearTierSelection = () => {
    setSelectedTier('free');
    setBillingInterval('monthly');
    setSelectedBundle(null);
    localStorage.removeItem('selectedTier');
    localStorage.removeItem('billingInterval');
    localStorage.removeItem('selectedBundle');
  };

  // Sync with localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedTier' && e.newValue) {
        const tier = e.newValue as TierName;
        if (tier === 'creator' || tier === 'studio' || tier === 'free') {
          setSelectedTier(tier);
        }
      }
      if (e.key === 'billingInterval' && e.newValue) {
        const interval = e.newValue as BillingInterval;
        if (interval === 'yearly' || interval === 'monthly') {
          setBillingInterval(interval);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <TierContext.Provider value={{ selectedTier, billingInterval, selectedBundle, setTierSelection, setBundleSelection, clearTierSelection }}>
      {children}
    </TierContext.Provider>
  );
}

export function useTier() {
  const context = useContext(TierContext);
  if (context === undefined) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
}
